import type { FunctionComponent, MutableRefObject } from 'react'
import { useEffect } from 'react'
import {
  LatLngTuple,
  control,
  Layer,
  Map,
  type Marker,
  type MapOptions,
  Control,
  Browser,
  type LeafletMouseEventHandlerFn,
  LatLngBounds,
} from 'leaflet'
import 'leaflet/dist/leaflet.css'
import styled from 'styled-components'
import useEvents, { type MoveHandler, type ZoomHandler } from './hooks/useEvents'
import useMap from './hooks/useMap'
import getCrsRd from './utils/getCrsRs'

export const DEFAULT_MAP_LAYER_URL = 'https://{s}.data.amsterdam.nl/topo_rd/{z}/{x}/{y}.png'

const SCALE_OPTIONS: Control.ScaleOptions = {
  position: 'bottomright',
  metric: true,
  imperial: false,
}

export const MAP_MAX_BOUNDS = new LatLngBounds([
  [52.25168, 4.64034] as LatLngTuple,
  [52.50536, 5.12737] as LatLngTuple,
])

export const MAP_DEFAULT_CENTER = [52.3731081, 4.8932945] as LatLngTuple

export const DEFAULT_MAP_OPTIONS = {
  attributionControl: false,
  center: MAP_DEFAULT_CENTER,
  crs: getCrsRd(),
  maxBounds: MAP_MAX_BOUNDS,
  maxZoom: 16,
  minZoom: 3,
  preferCanvas: !Browser.svg && !Browser.vml,
  zoom: 9,
  zoomControl: true,
}

export type MapRef = MutableRefObject<Map | null>
interface LeafletMapProps {
  layers?: Layer[]
  markers?: Marker[]
  scale?: boolean
  setInstance?: (map: Map) => void
  onClick?: LeafletMouseEventHandlerFn
  onMove?: MoveHandler
  onZoom?: ZoomHandler
  options?: MapOptions
}

const getLayerId = (layer: Layer): string | undefined => (layer as any).options.id

const containsLayer = (layers: Layer[], layer: Layer) =>
  layers.findIndex((item) => getLayerId(item) === getLayerId(layer)) !== -1

const MapContainer = styled.div`
  height: 100%;
`

const LeafletMap: FunctionComponent<LeafletMapProps> = ({
  layers,
  setInstance,
  markers,
  scale,
  onClick,
  onMove,
  onZoom,
  options,
}) => {
  const [containerRef, mapRef, drawnRef] = useMap(options ?? {}, setInstance)
  useEvents(mapRef, { zoom: options?.zoom, onClick, onMove, onZoom })

  useEffect(() => {
    const newDrawn: Layer[] = []

    drawnRef.current.forEach((layer) => {
      if (layers && containsLayer(layers, layer)) {
        newDrawn.push(layer)
      } else {
        layer.remove()
      }
    })

    if (layers && mapRef.current) {
      layers.forEach((layer) => {
        if (!containsLayer(drawnRef.current, layer)) {
          if (!getLayerId(layer)) {
            throw new Error('layer has no id')
          }

          layer.addTo(mapRef.current!)
          newDrawn.push(layer)
        }
      })
    }
    drawnRef.current = newDrawn
  }, [layers])

  useEffect(() => {
    if (markers && mapRef.current) {
      markers.forEach((marker) => marker.addTo(mapRef.current!))

      return () => markers.forEach((marker) => marker.remove())
    }
  }, [markers])

  useEffect(() => {
    if (scale && mapRef.current) {
      const scaleControl = control.scale(SCALE_OPTIONS)
      scaleControl.addTo(mapRef.current)

      return () => {
        scaleControl.remove()
      }
    }
  }, [scale])

  return <MapContainer ref={containerRef} />
}

export default LeafletMap
